import React from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../component/common/Loader";
import CONSTANT from "../../constant";
import { saveKpiReport, updateKpiReport, getKpiReport, submitKpiReport, getSendBackHistory, getSelfAppraisal, uploadKpiFile } from "../../actions/AppraisalCycleAction";
import { getAuthToken, formatDateDDMMYY, formatDateTimeDDMMYY } from "../../utils";

const ROMAN_LABELS = ['i)','ii)','iii)','iv)','v)','vi)','vii)','viii)','ix)','x)'];

class SelfAppraisalForm extends React.Component<any, any> {
  constructor(props: any) {
    super(props);

    const locationState = props.location?.state || {};

    this.state = {
      showLoader: false,
      selectedRecord: locationState.selectedRecord || null,
      employeeCode: locationState.employeeCode || '',

      isMouApplicable: locationState.isMouApplicable || false,
      kpiReportId: locationState.kpiReportId || null,
      kpiStatus: locationState.kpiStatus || null,

      mouTaskDescription: locationState.mouTaskDescription || '',
      mouWeightage: locationState.mouWeightage || (locationState.isMouApplicable ? 20 : 0),
      mouDeliverables: locationState.mouDeliverables || '',
      mouAchievement: locationState.mouAchievement || '',

      otherKeyTasks: locationState.otherKeyTasks || [],
      selfAppraisalSubmitted: false,
      sendBackRemarks: null,
      sendBackHistory: [],
      showHistory: false,
      kpiFile: null,
      kpiFileUrl: null,
      kpiFileName: null,
      isUploadingFile: false,
      validationErrors: {},
    };
  }

  unlisten: any = null;

  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = "Self Appraisal - Annual Work Plan - " + CONSTANT.organisation.name;

    if (!this.state.selectedRecord) {
      this.props.history.replace(CONSTANT.component.selfAppraisalList.url);
      return;
    }

    this.fetchKpiReport();

    this.unlisten = this.props.history.listen((location: any, action: string) => {
      if (action === 'POP' && location.pathname === CONSTANT.component.selfAppraisal.url) {
        this.fetchKpiReport();
      }
    });
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  fetchKpiReport = () => {
    const { employeeCode, selectedRecord } = this.state;
    if (!employeeCode || !selectedRecord?.startDate) return;

    this.setState({ showLoader: true });
    getKpiReport({ appraisalCycleId: selectedRecord?.cycle?.id, effectiveFrom: selectedRecord.startDate, employeeCode, reportType: 'employee' }).then((res: any) => {
      const report = res?.result || null;
      if (report) {
        const isMouApplicable = report.is_mou_applicable ?? this.state.isMouApplicable;
        const mouTask = report.tasks?.find((t: any) => t.particular === '*)') || null;
        const otherTasks = report.tasks
          ?.filter((t: any) => t.particular !== '*)')
          .map((t: any) => ({
            id: t.id,
            particular: t.particular,
            taskDescription: t.task_description || '',
            weightage: t.weightage || '',
            deliverables: t.deliverables || '',
            achievement: t.achievement || '',
          })) || [];

        this.setState({
          kpiReportId: report.id,
          kpiStatus: report.kpi_status || 'draft',
          isMouApplicable,
          mouTaskDescription: mouTask?.task_description || '',
          mouWeightage: mouTask?.weightage || (isMouApplicable ? 20 : 0),
          mouDeliverables: mouTask?.deliverables || '',
          mouAchievement: mouTask?.achievement || '',
          otherKeyTasks: otherTasks,
          sendBackRemarks: report.sendBackRemarks || null,
          sendBackHistory: report.sendBackHistory || [],
          kpiFileUrl: report.uploaded_file ? `${process.env.REACT_APP_BASE_URL}apar/kpi-report/get-file?kpiReportId=${report.id}&filename=${report.uploaded_file}` : null,
          kpiFileName: report.uploaded_file_original_name || report.uploaded_file || null,
        });

        // Check if self-appraisal has been submitted
        if (report.id) {
          getSelfAppraisal({ kpiReportId: report.id }).then((saRes: any) => {
            const saData = saRes?.result || null;
            if (saData && saData.is_submitted) {
              this.setState({ selfAppraisalSubmitted: true });
            }
          }).catch(() => {});
        }
      }
      this.setState({ showLoader: false });
    }).catch(() => {
      this.setState({ showLoader: false });
    });
  };

  onTextChange = (e: any) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleOtherTaskChange = (index: number, field: string, value: string) => {
    const updated = [...this.state.otherKeyTasks];
    if (field === 'weightage') {
      const maxTotal = this.state.isMouApplicable ? 55 : 75;
      const otherSum = updated.reduce((sum: number, item: any, i: number) =>
        i === index ? sum : sum + (parseFloat(item.weightage) || 0), 0);
      const num = parseFloat(value);
      if (value !== '' && !isNaN(num)) {
        if (num < 0) value = '0';
        else if (otherSum + num > maxTotal) value = String(maxTotal - otherSum);
      }
    }
    updated[index] = { ...updated[index], [field]: value };
    this.setState({ otherKeyTasks: updated });
  };

  addTask = () => {
    const { otherKeyTasks } = this.state;
    if (otherKeyTasks.length >= 10) return;
    const newTask = {
      id: `new-${Date.now()}`,
      particular: ROMAN_LABELS[otherKeyTasks.length],
      taskDescription: '',
      weightage: '',
      deliverables: '',
      achievement: '',
    };
    this.setState({ otherKeyTasks: [...otherKeyTasks, newTask] });
  };

  removeTask = (index: number) => {
    const updated = this.state.otherKeyTasks.filter((_: any, i: number) => i !== index);
    const relabeled = updated.map((task: any, i: number) => ({
      ...task,
      particular: ROMAN_LABELS[i],
    }));
    this.setState({ otherKeyTasks: relabeled });
  };

  getTotalWeightage = () =>
    this.state.otherKeyTasks.reduce(
      (sum: number, item: any) => sum + (parseFloat(item.weightage) || 0), 0
    );

  getMouWeightage        = () => (this.state.isMouApplicable ? (parseFloat(this.state.mouWeightage) || 0) : 0);
  getOtherTasksWeightage = () => (this.state.isMouApplicable ? 55 : 75);
  getGrandTotal          = () => this.getMouWeightage() + this.getOtherTasksWeightage();

  buildTasksPayload = () => {
    const {
      isMouApplicable, mouTaskDescription, mouWeightage: mouWt,
      mouDeliverables, mouAchievement, otherKeyTasks,
    } = this.state;

    const tasks = otherKeyTasks.map((task: any) => ({
      id: task.id?.toString().startsWith('new-') ? undefined : task.id,
      particular: task.particular,
      taskDescription: task.taskDescription,
      weightage: parseFloat(task.weightage) || 0,
      deliverables: task.deliverables,
      achievement: task.achievement,
    }));

    if (isMouApplicable) {
      tasks.unshift({
        particular: '*)',
        taskDescription: mouTaskDescription,
        weightage: parseFloat(mouWt) || 0,
        deliverables: mouDeliverables,
        achievement: mouAchievement,
      });
    }

    return tasks;
  };

  saveKpi = (callback?: () => void) => {
    const { selectedRecord, employeeCode, kpiReportId, isMouApplicable } = this.state;
    const tasks = this.buildTasksPayload();

    this.setState({ showLoader: true });

    if (kpiReportId) {
      updateKpiReport({ kpiReportId, isMouApplicable, tasks }).then(() => {
        this.setState({ showLoader: false });
        if (callback) callback();
      }).catch(() => {
        this.setState({ showLoader: false });
      });
    } else {
      const payload = {
        appraisalCycleId: selectedRecord?.cycle?.id,
        employeeCode,
        effectiveFrom: selectedRecord?.startDate,
        isMouApplicable,
        reportType: 'employee',
        tasks,
      };
      saveKpiReport(payload).then((res: any) => {
        const newKpiReportId = res?.result?.id || res?.result?.kpiReportId || null;
        this.setState({ showLoader: false, kpiReportId: newKpiReportId, kpiStatus: 'draft' });
        if (callback) callback();
      }).catch(() => {
        this.setState({ showLoader: false });
      });
    }
  };

  validateKpiForSubmit = () => {
    const {
      isMouApplicable, mouTaskDescription, mouWeightage, mouDeliverables,
      otherKeyTasks,
    } = this.state;

    const errors: any = {};

    if (isMouApplicable) {
      if (!String(mouTaskDescription).trim()) errors.mouTaskDescription = true;
      if (!(parseFloat(mouWeightage) > 0)) errors.mouWeightage = true;
      if (!String(mouDeliverables).trim()) errors.mouDeliverables = true;
    }

    otherKeyTasks.forEach((task: any, index: number) => {
      if (!String(task.taskDescription).trim()) errors[`task-${index}-taskDescription`] = true;
      if (!(parseFloat(task.weightage) > 0)) errors[`task-${index}-weightage`] = true;
      if (!String(task.deliverables).trim()) errors[`task-${index}-deliverables`] = true;
    });

    if (!this.state.kpiFileUrl) errors.kpiFile = true;

    return errors;
  };

  validateAchievements = () => {
    const { isMouApplicable, mouAchievement, otherKeyTasks } = this.state;
    const errors: any = {};

    if (isMouApplicable && !String(mouAchievement).trim()) {
      errors.mouAchievement = true;
    }

    otherKeyTasks.forEach((task: any, index: number) => {
      if (!String(task.achievement).trim()) errors[`task-${index}-achievement`] = true;
    });

    if (!this.state.kpiFileUrl) errors.kpiFile = true;

    return errors;
  };

  handleSubmitForApproval = () => {
    const errors = this.validateKpiForSubmit();
    if (Object.keys(errors).length > 0) {
      this.setState({ validationErrors: errors });
      alert(errors.kpiFile
        ? 'Please fill all the highlighted required fields and upload the supporting document before submitting.'
        : 'Please fill all the highlighted required fields before submitting.');
      return;
    }
    this.setState({ validationErrors: {} });

    if (!window.confirm('Are you sure you want to submit the KPI report for approval? You will not be able to edit it after submission.')) return;

    this.saveKpi(() => {
      const { kpiReportId } = this.state;
      if (!kpiReportId) return;

      this.setState({ showLoader: true });
      submitKpiReport({ kpiReportId }).then((res: any) => {
        if (res?.result) {
          this.setState({ kpiStatus: 'submitted', showLoader: false });
          alert('KPI report submitted for approval successfully.');
        } else {
          this.setState({ showLoader: false });
        }
      }).catch(() => {
        this.setState({ showLoader: false });
      });
    });
  };

  goToDetails = () => {
    const errors = this.validateAchievements();
    if (Object.keys(errors).length > 0) {
      this.setState({ validationErrors: errors });
      alert(errors.kpiFile
        ? 'Please fill all the highlighted required fields and upload the supporting document before proceeding.'
        : 'Please fill all the highlighted required fields before proceeding.');
      return;
    }
    this.setState({ validationErrors: {} });

    this.saveKpi(() => {
      this.navigateToDetails();
    });
  };

  navigateToDetails = () => {
    const {
      selectedRecord, kpiReportId, kpiStatus,
      mouTaskDescription, mouWeightage: mouWt, mouDeliverables, mouAchievement,
      otherKeyTasks,
    } = this.state;
    this.props.history.push({
      pathname: CONSTANT.component.selfAppraisalDetails.url,
      state: {
        selectedRecord,
        kpiReportId,
        kpiStatus,
        mouTaskDescription,
        mouWeightage: mouWt,
        mouDeliverables,
        mouAchievement,
        otherKeyTasks,
      },
    });
  };

  openKpiFile = () => {
    const { kpiFileUrl } = this.state;
    if (!kpiFileUrl) return;
    const token = getAuthToken();
    fetch(kpiFileUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => alert('Failed to open file.'));
  };

  handleKpiFileChange = (e: any) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    const doUpload = (kpiReportId: any) => {
      if (!kpiReportId) return;
      this.setState({ isUploadingFile: true });
      uploadKpiFile({ kpiReportId, file }).then((res: any) => {
        if (res?.result) {
          const r = res.result;
          const storedName = r.uploaded_file || r.file_name || '';
          const kpiReportId = this.state.kpiReportId;
          const newFileUrl = storedName ? `${process.env.REACT_APP_BASE_URL}apar/kpi-report/get-file?kpiReportId=${kpiReportId}&filename=${storedName}` : null;
          this.setState({
            kpiFileUrl: newFileUrl,
            kpiFileName: r.uploaded_file_original_name || r.original_name || file.name,
            kpiFile: null,
            isUploadingFile: false,
            validationErrors: { ...this.state.validationErrors, kpiFile: false },
          });
        } else {
          this.setState({ isUploadingFile: false });
        }
      }).catch(() => {
        this.setState({ isUploadingFile: false });
      });
    };

    if (this.state.kpiReportId) {
      doUpload(this.state.kpiReportId);
    } else {
      this.saveKpi(() => doUpload(this.state.kpiReportId));
    }
  };

  render() {
    const {
      showLoader, selectedRecord,
      isMouApplicable, kpiReportId, kpiStatus,
      mouTaskDescription, mouWeightage: mouWeightageState, mouDeliverables, mouAchievement,
      otherKeyTasks, validationErrors,
    } = this.state;

    const mouWeightage        = this.getMouWeightage();
    const otherTasksWeightage = this.getOtherTasksWeightage();
    const grandTotal          = this.getGrandTotal();
    const totalWeightage      = this.getTotalWeightage();

    const selectedCycle = selectedRecord?.cycle;
    const isCycleActive = selectedRecord?.periodStatus === 'Active';

    // KPI fields (task, weightage, deliverables): editable when draft, or sent back by RM
    const isKpiEditable = !kpiStatus || kpiStatus === 'draft' || kpiStatus === 'sent_back_by_rm';
    // Achievement fields: editable only when cycle is active AND kpi is fully approved AND self-appraisal not yet submitted
    const isAchievementEditable = isCycleActive && (kpiStatus === 'rvm_approved') && !this.state.selfAppraisalSubmitted;
    // Show the form always (employee creates it, no need for HR upload)
    const showForm = true;

    return (
      <div className="container mx-auto px-4 py-3">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">

                <div className="row mb-3">
                  <div className="col-md-8">
                    <h1 className="page-title">Self Appraisal - Annual Work Plan</h1>
                    {selectedRecord && (
                      <p className="text-muted mb-0">
                        <strong>{selectedCycle?.cycle_name || selectedCycle?.financial_year}</strong>
                        &nbsp;&middot;&nbsp;
                        {formatDateDDMMYY(selectedRecord.startDate)}{selectedRecord.endDate ? ` to ${formatDateDDMMYY(selectedRecord.endDate)}` : " onwards"}
                        &nbsp;&middot;&nbsp;
                        <span className={`badge ${selectedRecord.isOriginal ? 'badge-success' : 'badge-warning'}`}>
                          {selectedRecord.periodType}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="col-md-4 text-right">
                    <button
                      type="button"
                      className="btn btn-sm cancel-button"
                      onClick={() => this.props.history.push(CONSTANT.component.selfAppraisalList.url)}
                    >
                      <i className="fa fa-arrow-left mr-2" />Back to Periods
                    </button>
                  </div>
                </div>

                {/* Send-back banner */}
                {kpiStatus === 'sent_back_by_rm' && this.state.sendBackRemarks && (
                  <div className="alert alert-danger mt-2">
                    <strong><i className="fa fa-exclamation-triangle mr-1" /> Your KPI report has been sent back by the Reporting Manager:</strong>{' '}
                    {this.state.sendBackRemarks.remarks}
                    <div className="text-muted small mt-1">
                      {this.state.sendBackRemarks.created_at && `On: ${formatDateTimeDDMMYY(this.state.sendBackRemarks.created_at)}`}
                    </div>
                    <div className="mt-2">Please review, make corrections, and re-submit.</div>
                  </div>
                )}

                {/* Send-back history */}
                {this.state.sendBackHistory.length > 0 && (
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => this.setState({ showHistory: !this.state.showHistory })}
                    >
                      <i className="fa fa-history mr-1" />
                      {this.state.showHistory ? 'Hide' : 'View'} Send-Back History ({this.state.sendBackHistory.length})
                    </button>
                    {this.state.showHistory && (
                      <div className="mt-2">
                        {this.state.sendBackHistory.map((item: any, i: number) => (
                          <div key={item.id || i} className="alert alert-light border py-2 mb-1">
                            <strong>{item.send_back_by === 'reporting' ? 'Reporting Manager' : 'Reviewing Manager'}</strong>
                            {' sent back to '}
                            <strong>{item.send_back_to === 'employee' ? 'Employee' : 'Reporting Manager'}</strong>
                            <span className="text-muted small ml-2">
                              {item.created_at && `(${formatDateTimeDDMMYY(item.created_at)})`}
                            </span>
                            <div className="mt-1">{item.remarks}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Status alerts */}
                {kpiStatus === 'submitted' && (
                  <div className="alert alert-info mt-2">
                    Your KPI report has been submitted and is pending approval from Reporting Manager.
                  </div>
                )}
                {kpiStatus === 'rm_approved' && (
                  <div className="alert alert-info mt-2">
                    Your KPI report has been approved by Reporting Manager and is pending approval from Reviewing Manager.
                  </div>
                )}
                {kpiStatus === 'rvm_approved' && !isCycleActive && (
                  <div className="alert alert-success mt-2">
                    Your KPI report has been fully approved. You can fill achievements once the cycle is active.
                  </div>
                )}
                {kpiStatus === 'rvm_approved' && isCycleActive && !this.state.selfAppraisalSubmitted && (
                  <div className="alert alert-success mt-2">
                    Your KPI report is approved. Please fill in your achievements below.
                  </div>
                )}
                {this.state.selfAppraisalSubmitted && (
                  <div className="alert alert-success mt-2">
                    <i className="fa fa-check-circle mr-1" /> Your self appraisal has been submitted and is now view-only.
                  </div>
                )}
                {isKpiEditable && !kpiReportId && (
                  <div className="alert alert-info mt-2">
                    Please add your KPI tasks below and submit for approval.
                  </div>
                )}

                {showLoader && <Loader />}

                {showForm && <div>
                  <h2 className="fs-5 fw-bold border-bottom pb-2 mb-4">
                    Annual Work Plan and Achievement
                  </h2>

                  <div className="table-responsive mb-2">
                    <table className="table table-bordered table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '30%' }}>Tasks to be Performed</th>
                          <th style={{ width: '10%' }}>Weightage</th>
                          <th style={{ width: '22%' }}>Deliverables</th>
                          <th style={{ width: '22%' }}>Achievement</th>
                          {isKpiEditable && <th style={{ width: '6%' }}>Action</th>}
                        </tr>
                      </thead>
                      <tbody>

                        {/* MOU Section */}
                        <tr className="table-secondary">
                          <td colSpan={isKpiEditable ? 5 : 4} className="fw-semibold">
                            I – MoU Targets
                            {isMouApplicable && (
                              <span className="text-muted fw-normal ms-2 small">(Weightage: {mouWeightage}%)</span>
                            )}
                          </td>
                        </tr>

                        {isMouApplicable ? (
                          <tr>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="text-muted fw-semibold" style={{ minWidth: '36px' }}>*)</span>
                                <textarea
                                  className={`form-control form-control-sm ${validationErrors.mouTaskDescription ? 'is-invalid' : ''}`}
                                  name="mouTaskDescription"
                                  value={mouTaskDescription}
                                  onChange={this.onTextChange}
                                  placeholder="Task Description"
                                  disabled={!isKpiEditable}
                                  rows={2}
                                  style={{ resize: 'vertical', minHeight: '38px' }}
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                type="number"
                                className={`form-control form-control-sm text-center ${validationErrors.mouWeightage ? 'is-invalid' : ''}`}
                                name="mouWeightage"
                                min="0"
                                max="20"
                                value={mouWeightageState}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  const num = parseFloat(val);
                                  if (val !== '' && !isNaN(num)) {
                                    if (num > 20) val = '20';
                                    if (num < 0) val = '0';
                                  }
                                  this.setState({ mouWeightage: val });
                                }}
                                placeholder="0"
                                disabled={!isKpiEditable}
                              />
                            </td>
                            <td>
                              <textarea
                                className={`form-control form-control-sm ${validationErrors.mouDeliverables ? 'is-invalid' : ''}`}
                                name="mouDeliverables"
                                value={mouDeliverables}
                                onChange={this.onTextChange}
                                placeholder="Deliverables"
                                disabled={!isKpiEditable}
                                rows={2}
                                style={{ resize: 'vertical', minHeight: '38px' }}
                              />
                            </td>
                            <td>
                              <textarea
                                className={`form-control form-control-sm ${validationErrors.mouAchievement ? 'is-invalid' : ''}`}
                                name="mouAchievement"
                                value={mouAchievement}
                                onChange={this.onTextChange}
                                placeholder="Achievement"
                                disabled={!isAchievementEditable}
                                rows={2}
                                style={{ resize: 'vertical', minHeight: '38px' }}
                              />
                            </td>
                            {isKpiEditable && <td></td>}
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan={isKpiEditable ? 5 : 4} className="text-muted fst-italic ps-3 small">MoU not applicable</td>
                          </tr>
                        )}

                        {/* Other Tasks Section */}
                        <tr className="table-secondary">
                          <td colSpan={isKpiEditable ? 5 : 4} className="fw-semibold">
                            II – Other Key Assigned Tasks flowing from KPI
                            <span className="text-muted fw-normal ms-2 small">(Weightage: {otherTasksWeightage}%)</span>
                          </td>
                        </tr>

                        {otherKeyTasks.length === 0 ? (
                          <tr>
                            <td colSpan={isKpiEditable ? 5 : 4} className="text-muted fst-italic ps-3 small">
                              No tasks added yet. {isKpiEditable && 'Click "Add Task" to add a new task.'}
                            </td>
                          </tr>
                        ) : (
                          otherKeyTasks.map((task: any, index: number) => (
                            <tr key={task.id || index}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-muted fw-semibold" style={{ minWidth: '36px' }}>{task.particular}</span>
                                  <textarea
                                    className={`form-control form-control-sm ${validationErrors[`task-${index}-taskDescription`] ? 'is-invalid' : ''}`}
                                    value={task.taskDescription}
                                    onChange={(e) => this.handleOtherTaskChange(index, 'taskDescription', e.target.value)}
                                    placeholder="Task Description"
                                    disabled={!isKpiEditable}
                                    rows={2}
                                    style={{ resize: 'vertical', minHeight: '38px' }}
                                  />
                                </div>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className={`form-control form-control-sm text-center ${validationErrors[`task-${index}-weightage`] ? 'is-invalid' : ''}`}
                                  value={task.weightage}
                                  onChange={(e) => this.handleOtherTaskChange(index, 'weightage', e.target.value)}
                                  placeholder="0"
                                  disabled={!isKpiEditable}
                                />
                              </td>
                              <td>
                                <textarea
                                  className={`form-control form-control-sm ${validationErrors[`task-${index}-deliverables`] ? 'is-invalid' : ''}`}
                                  value={task.deliverables}
                                  onChange={(e) => this.handleOtherTaskChange(index, 'deliverables', e.target.value)}
                                  placeholder="Deliverables"
                                  disabled={!isKpiEditable}
                                  rows={2}
                                  style={{ resize: 'vertical', minHeight: '38px' }}
                                />
                              </td>
                              <td>
                                <textarea
                                  className={`form-control form-control-sm ${validationErrors[`task-${index}-achievement`] ? 'is-invalid' : ''}`}
                                  value={task.achievement}
                                  onChange={(e) => this.handleOtherTaskChange(index, 'achievement', e.target.value)}
                                  placeholder="Achievement"
                                  disabled={!isAchievementEditable}
                                  rows={2}
                                  style={{ resize: 'vertical', minHeight: '38px' }}
                                />
                              </td>
                              {isKpiEditable && (
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => this.removeTask(index)}
                                    title="Remove Task"
                                  >
                                    <i className="fa fa-trash" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}

                        {/* Add Task button row */}
                        {isKpiEditable && otherKeyTasks.length < 10 && (
                          <tr>
                            <td colSpan={5}>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={this.addTask}
                              >
                                <i className="fa fa-plus me-1" /> Add Task
                              </button>
                            </td>
                          </tr>
                        )}

                        <tr className="table-light fw-semibold">
                          <td className="text-end pe-3">
                            Total ({ROMAN_LABELS[0]} to {ROMAN_LABELS[Math.max(otherKeyTasks.length - 1, 0)]})
                          </td>
                          <td className="text-center">
                            {totalWeightage > 0 ? totalWeightage.toFixed(0) : ''}
                          </td>
                          <td></td><td></td>
                          {isKpiEditable && <td></td>}
                        </tr>

                        <tr className="table-secondary fw-bold">
                          <td className="text-end pe-3"><span className="me-2">III</span> Grand Total</td>
                          <td className="text-center">{grandTotal}</td>
                          <td></td><td></td>
                          {isKpiEditable && <td></td>}
                        </tr>

                      </tbody>
                    </table>
                  </div>

                  <div className="text-muted mt-3 mb-3">
                    <p className="mb-1"><strong>a.</strong> Deliverables refer to quantitative or financial targets or verbal description of expected outputs. The deliverables and the weights for individual key assigned tasks will be decided by the Reporting Authority after consulting the officer reported upon within two months from the start of the period of report. The Reporting Authority shall send a copy of the details of the finally agreed key assigned tasks and their relative weights targets to the Nodal officer by 30th June.</p>
                    <p className="mb-1"><strong>b.</strong> Actual achievements refer to the achievements against the specified deliverables in respect of each task. No explanations for divergences are to be given in this table.</p>
                    <p className="mb-1"><strong>c.</strong> The weightage for MoU targets is 75 for Chief Executives, 50 for Functional Directors and Executives at E9 &amp; E8 level.</p>
                    <p className="mb-1"><strong>d.</strong> The final MoU score based on audited accounts conveyed by DPE should be filled in this space.</p>
                    <p className="mb-0"><strong>e.</strong> The weightage for other assigned tasks flowing from KPI is nil for Chief Executives and 25 for Functional Director &amp; Executives at E9 &amp; E8 level.</p>
                  </div>

                  {/* KPI File Upload */}
                  <div className={`card mt-4 mb-3 ${validationErrors.kpiFile ? 'border-danger' : 'border'}`}>
                    <div className="card-header bg-light fw-semibold">
                      <i className="fa fa-paperclip mr-2" />Upload Supporting Document
                      {(isKpiEditable || isAchievementEditable) && <span className="text-danger ml-1">*</span>}
                    </div>
                    <div className="card-body">
                      {this.state.isUploadingFile && (
                        <div className="text-muted small mb-2"><i className="fa fa-spinner fa-spin mr-1" />Uploading...</div>
                      )}
                      {this.state.kpiFileUrl && !this.state.isUploadingFile && (
                        <div className="d-flex align-items-center gap-2 mb-3 p-2 border rounded bg-light">
                          <i className="fa fa-file-o text-secondary fa-lg" />
                          <span className="fw-semibold flex-grow-1">{this.state.kpiFileName || 'Uploaded File'}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={this.openKpiFile}
                          >
                            <i className="fa fa-external-link me-1" />Open
                          </button>
                        </div>
                      )}
                      {(isKpiEditable || isAchievementEditable) && (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <input
                            type="file"
                            className={`form-control form-control-sm ${validationErrors.kpiFile ? 'is-invalid' : ''}`}
                            style={{ maxWidth: '320px' }}
                            accept=".pdf"
                            onChange={this.handleKpiFileChange}
                            disabled={this.state.isUploadingFile}
                          />
                          {/* <span className="text-muted small">File uploads automatically on selection</span> */}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => this.props.history.push(CONSTANT.component.selfAppraisalList.url)}>
                      Cancel
                    </button>

                    {/* Save draft (when KPI is still editable, or when filling achievements) */}
                    {(isKpiEditable || isAchievementEditable) && (
                      <button type="button" className="btn btn-secondary" onClick={() => this.saveKpi(() => alert('KPI report saved as draft.'))} disabled={showLoader}>
                        {showLoader ? "Saving..." : "Save Draft"}
                      </button>
                    )}

                    {/* Submit for approval (when KPI is editable and has tasks) */}
                    {isKpiEditable && otherKeyTasks.length > 0 && (
                      <button type="button" className="btn btn-success" onClick={this.handleSubmitForApproval} disabled={showLoader}>
                        <i className="fa fa-paper-plane mr-1" /> {kpiStatus === 'sent_back_by_rm' ? 'Re-submit for Approval' : 'Submit for Approval'}
                      </button>
                    )}

                    {/* Save & Next to details (when KPI is approved and cycle active - for filling achievements) */}
                    {isAchievementEditable && (
                      <button type="button" className="btn btn-primary" onClick={this.goToDetails} disabled={showLoader}>
                        {showLoader ? "Saving..." : "Save & Next"} <i className="fa fa-arrow-right ml-1" />
                      </button>
                    )}

                    {/* View details (when self-appraisal already submitted) */}
                    {this.state.selfAppraisalSubmitted && (
                      <button type="button" className="btn btn-primary" onClick={() => this.navigateToDetails()}>
                        View Details <i className="fa fa-arrow-right ml-1" />
                      </button>
                    )}
                  </div>
                </div>}

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(SelfAppraisalForm);
